import { createContext, useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { configureGeneratedApiAuth } from 'api/generatedClient';
import { publicApi } from 'api';
import { useGetUser, User } from 'api/hooks/private/users/GetUser';

import { createDedupedRefresh } from 'context/createDedupedRefresh';
import useAxiosInterceptors from 'hooks/useAxiosInterceptors';
import useStateRef from 'hooks/useStateRef';

interface PropTypes {
  children: JSX.Element;
}

/**
 * Response object returned by the refresh token endpoint.
 *
 * @interface RefreshResponse
 */
interface RefreshResponse {
  /**
   * The new access token if refresh is successful, or null if no token is returned.
   *
   * @type {string | null}
   */
  accessToken: string | null;
}

/**
 * Represents the authentication state and actions available throughout the app.
 *
 * Provides information about the current user's authentication status, user details,
 * access token management, logout functionality, and persistence settings.
 *
 * @interface AuthContextType
 */
interface AuthContextType {
  /**
   * The current access token used for authenticated requests.
   * @type {string}
   */
  accessToken?: string;

  /**
   * The ID of the tenant (organization or group) the user belongs to.
   * @type {string}
   */
  tenantId?: string;

  /**
   * The unique identifier of the user.
   * @type {string}
   */
  userId?: string;

  /**
   * The role of the user within the system.
   * @type {'ADMIN' | 'INSTRUCTOR' | 'STUDENT'}
   */
  userRole?: 'ADMIN' | 'INSTRUCTOR' | 'STUDENT';

  /**
   * The authenticated user object.
   * @type {User | undefined}
   */
  user?: User;

  /**
   * Flag indicating whether the authentication state is initializing for the first time
   * (e.g., refreshing access token on app startup).
   * @type {boolean}
   */
  isInitializing: boolean;

  /**
   * Updates the current access token and any derived state (e.g., payload, user).
   * @type {(token: string | undefined) => void}
   */

  setAccessToken: (token: string | undefined) => void;

  /**
   * Refreshes the access token by making a request to the refresh endpoint.
   * @returns {Promise<string | null>} A promise resolving to the new access token string,
   * null if the user is not logged in, or throws on unexpected errors.
   */
  refresh: () => Promise<string | null>;

  /**
   * Logs out the current user and clears authentication state.
   * @returns {Promise<void>} A promise that resolves when logout is complete.
   */
  logout: () => Promise<void>;

  /**
   * Indicates whether authentication state should persist across sessions.
   * @type {boolean}
   */
  persist: boolean;
}

const AuthContext = createContext<AuthContextType>({
  accessToken: undefined,
  tenantId: undefined,
  userId: undefined,
  userRole: undefined,
  user: undefined,
  isInitializing: true,
  setAccessToken: () => {},
  refresh: async () => null,
  logout: async () => {},
  persist: false,
});

function AuthProvider({ children }: PropTypes) {
  // flag to indicate the auth context is being initialized
  const [isInitializing, setIsInitializing] = useState(true);

  // store the access token in state and a ref for immediate access in axios interceptors
  const [accessToken, setAccessToken, accessTokenRef] = useStateRef<string | undefined>(undefined);

  const refresh = useMemo(
    () =>
      createDedupedRefresh(async () => {
        try {
          // make a request to the refresh endpoint to get a new access token
          const response = await publicApi.get<RefreshResponse>('/public/auth/refresh', {
            withCredentials: true,
          });

          // get the new access token from response
          const { accessToken } = response.data;

          // update auth context with the new access token or set it to undefined if no token is returned
          setAccessToken(accessToken ?? undefined);

          // return the new access token
          return accessToken;
        } catch (error) {
          // if the request returned a 403 status code, the user is not logged in
          if (isAxiosError(error) && error.response?.status === 403) {
            return null;
          }

          // throw the unexpected error
          throw error;
        } finally {
          setIsInitializing(false);
        }
      }),
    [setAccessToken],
  );

  // setup axios interceptors to handle authorization and automatic token refreshing
  useAxiosInterceptors({
    accessTokenRef,
    setAccessToken,
    refresh,
  });

  // decode the access token payload
  const accessTokenPayload = useMemo(() => {
    // if there is an access token
    if (accessToken) {
      // decode and return the payload
      return jwtDecode<AccessTokenPayload>(accessToken);
    }

    // if there is no access token, return undefined
    return undefined;
  }, [accessToken]);

  // fetch the logged-in user based on the access token payload
  const user = useGetUser({
    variables: {
      id: accessTokenPayload?.userId ?? '',
    },
    enabled: !!accessTokenPayload?.userId,
  });

  const logout = async () => {
    try {
      // make logout request
      await publicApi.get('/public/auth/logout');

      // clear the access token
      setAccessToken(undefined);
    } catch (error) {
      // if an axios error was thrown
      if (isAxiosError(error)) {
        if (!error?.response) {
          // no response from the server
          alert('No Server Response');
        } else if (error.response?.data?.message) {
          // incorrect username or password
          alert(error.response?.data?.message);
        }
      }
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    // if there is no access token on first render, try to refresh it
    if (!accessTokenRef.current) {
      refresh();
    }
  }, [accessTokenRef, refresh]);

  useEffect(() => {
    configureGeneratedApiAuth({
      getAccessToken: () => accessTokenRef.current,
      refreshAccessToken: refresh,
    });
  }, [accessTokenRef, refresh]);

  useEffect(() => {
    // if there is an access token, set isInitializing to false
    if (accessToken) {
      setIsInitializing(false);
    }
  }, [accessToken]);

  useEffect(() => {
    // if user was fetched set sentry user context info
    if (user.isSuccess && user.data) {
      Sentry.setUser({
        tenantId: user.data.tenant_id,
        id: user.data.id,
        fullName: [user.data.first_name, user.data.last_name].filter(Boolean).join(' ') || undefined,
        email: user.data.email ?? undefined,
        username: user.data.username ?? undefined,
      });
    } else {
      // clear user when logged out
      Sentry.setUser(null);
    }
  }, [user.data]);

  return (
    <AuthContext.Provider
      value={{
        accessToken: accessToken,
        tenantId: accessTokenPayload?.tenantId,
        userId: accessTokenPayload?.userId,
        userRole: accessTokenPayload?.userRole,
        user: user.data ?? undefined,
        isInitializing: isInitializing,
        setAccessToken: setAccessToken,
        refresh: refresh,
        logout: logout,
        persist: true,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export type { AuthContextType };
export { AuthContext, AuthProvider };
